import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import StateSelect from "@/components/StateSelect";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { API } from "@/App";

const EMPTY_FORM = {
  name: "",
  description: "",
  category: "Food & Beverage",
  custom_category: "",
  city: "",
  state: "",
  address: "",
  zip_code: "",
  phone: "",
  email: "",
  website: "",
  image_url: "",
  is_verified: true,
};

export default function AdminAddBusinessDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/categories`)
      .then((res) => setCategories(res.data?.categories || []))
      .catch(() => setCategories([]));
  }, [open]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.description.trim()) {
      toast.error("Name and description are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.category !== "Other") payload.custom_category = "";
      const res = await axios.post(
        `${API}/admin/businesses`,
        payload,
        { withCredentials: true }
      );
      toast.success(`Added "${res.data?.business?.name || form.name}"`);
      setForm(EMPTY_FORM);
      setOpen(false);
      if (onCreated) onCreated(res.data?.business);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create business");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
          data-testid="admin-add-business-trigger"
        >
          <Plus className="w-4 h-4 mr-1" /> Add Business
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-add-business-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-tbc-navy">
            Add Business to Directory
          </DialogTitle>
          <p className="text-sm text-gray-500">
            Seed the directory with Black-owned businesses you want to spotlight. You&apos;ll be the listed owner — you can edit or delete this from the admin panel later.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="text-sm font-medium text-gray-700">Business Name *</label>
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
              data-testid="admin-business-name"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Description *</label>
            <Textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={3}
              required
              placeholder="What does this business do? Who do they serve?"
              data-testid="admin-business-description"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Category *</label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="admin-business-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(categories.length ? categories : ["Food & Beverage", "Other"]).map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.category === "Other" && (
              <div>
                <label className="text-sm font-medium text-gray-700">Specify your field *</label>
                <Input
                  value={form.custom_category}
                  onChange={(e) => set("custom_category", e.target.value)}
                  placeholder="e.g. DJ services, auto detailing, tax prep"
                  data-testid="admin-custom-category-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This shows up on your business profile in place of &ldquo;Other&rdquo;.
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">City</label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">State</label>
              <StateSelect
                value={form.state}
                onChange={(code) => set("state", code)}
                testId="admin-add-state-select"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">ZIP</label>
              <Input value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Street Address</label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Phone</label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Website</label>
            <Input
              value={form.website}
              onChange={(e) => set("website", e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Image URL</label>
            <Input
              value={form.image_url}
              onChange={(e) => set("image_url", e.target.value)}
              placeholder="https://..."
            />
            <p className="text-xs text-gray-400 mt-1">
              Paste a public image URL for now. Upload-from-device in the next iteration.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm text-tbc-navy">
            <Checkbox
              checked={form.is_verified}
              onCheckedChange={(v) => set("is_verified", !!v)}
              data-testid="admin-business-verified"
            />
            Award gold verification badge immediately
          </label>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
              data-testid="admin-business-submit"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…
                </>
              ) : (
                "Add Business"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
